/*
 * Cottomix theme — infinite scroll for collection + search (cm-infinite.js)
 *
 * Purpose: QC-OPUS-R1 #23 — no numbered pagination on any collection or
 * search page. The next page loads automatically: an IntersectionObserver on
 * a sentinel (rootMargin 0px 0px max(700px, 1.5 viewports) 0px, so the fetch
 * starts before the user reaches the end; one page at a time, M-perf) fetches the next page through the Shopify Section
 * Rendering API (same path + current query string + page=N + section_id) and
 * appends only the new <li> cards to the grid — never replaces. Appended
 * cards carry .cm-reveal (server markup) and re-enter through the shared
 * card-entry tokens; theme.js re-observes injected nodes itself.
 *
 * Markup contract (snippets/pagination.liquid, mode: 'infinite'):
 *   [data-cm-inf]        footer root; data-inf-pages (total pages),
 *                        data-inf-max (max pages rebuilt on back navigation),
 *                        data-inf-loaded-tpl ("Loaded __COUNT__ more products")
 *   [data-cm-inf-stage]  wraps skeleton + sentinel (hidden when no next page)
 *   [data-cm-inf-sk]     skeleton row, [hidden] while idle
 *   [data-cm-inf-sentinel] 1px observation target
 *   [data-cm-inf-status] role=status aria-live=polite, never focus-stealing
 *   [data-cm-inf-error]  error line, [hidden] until a fetch fails
 *   [data-cm-inf-more]   real <a href="?page=N+1"> Load more: no-JS / no-IO /
 *                        fetch-error fallback; CSS hides it while JS runs
 *   [data-cm-inf-end]    "You've seen all N products"; server renders it
 *                        visible when there is no next page
 * The footer sits directly after the results list (ul.cm-grid or
 * ul.cm-result-list) inside [data-cm-region="results"], so a filter / sort
 * swap (cm-facets.js) re-renders it at page 1; this file re-inits from the
 * CM bus event collection:updated.
 *
 * Behaviour:
 *   - URL: history.replaceState keeps ?page=<last loaded page> alongside the
 *     other params (filters, sort_by, q, type). No history entries, no jumps.
 *   - Back navigation: {page, scrollY} is saved to sessionStorage
 *     ("cm-inf:" + pathname + search) on throttled scroll / pagehide /
 *     visibilitychange. On load with ?page=N and a saved entry,
 *     scrollRestoration goes manual, page 1 is fetched once (the server
 *     rendered page N), pages 2..min(N, data-inf-max) are appended
 *     sequentially, then the saved scrollY is restored. Without a saved entry
 *     (deep link) the server-rendered page stays and forward loading
 *     continues from it.
 *   - Dedupe: product handles seen on the page (plus per append) never append
 *     twice; promo tiles stay page-1-only.
 *   - Fallbacks: no IntersectionObserver → the Load more link shows and
 *     appends on click; a failed fetch shows the error line and the Load more
 *     link again (clicking it retries the same page). JS off → the link is a
 *     plain full-page navigation.
 *   - Reduced motion: nothing here animates; the appended cards' reveal is
 *     neutralised by base.css, the skeleton shimmer by section-collection.css.
 * Owner: COLLECTION (fid-e11).
 */
(function () {
  'use strict';

  /* L2 QC item 6 started the next fetch >= 2.5 viewports ahead. M-perf: ONE page at a
     time, the next one, once the sentinel is within ~1.5 viewports (min 700px) of the
     viewport; the reserved space (below) covers the fast scroller and the scroll anchor
     (anchorOf / holdAnchor) covers pages that sort ABOVE what is on screen. The grouped
     search view (data-inf-eager) is the one exception, see eagerAll. */
  var ROOT_PX = Math.max(700, Math.round((window.innerHeight || 800) * 1.5));
  var ROOT_MARGIN = '0px 0px ' + ROOT_PX + 'px 0px';
  var STORE_PREFIX = 'cm-inf:';
  var SAVE_THROTTLE_MS = 200;

  var LIVE = new Set(); // footer instances still connected
  var saveBound = false;
  var saveArmed = false; // first real user input — until then the scroll
  // events belong to the browser's own history restoration and must NOT
  // clobber the stored {page, y} before restore() has read it

  /* ------------------------------------------------------------ *
   * URL helpers — page param over the CURRENT query string
   * ------------------------------------------------------------ */
  function pageParam() {
    var raw = new URLSearchParams(location.search).get('page');
    var n = parseInt(raw, 10);
    return isNaN(n) || n < 1 ? 1 : n;
  }

  function urlWithPage(page) {
    var url = new URL(location.href);
    url.searchParams.set('page', String(page));
    url.searchParams.delete('section_id');
    return url.pathname + url.search;
  }

  /* ------------------------------------------------------------ *
   * Fetch — section render for one page, returns its list element
   * ------------------------------------------------------------ */
  function fetchPage(st, page) {
    var url = urlWithPage(page);
    var sep = url.indexOf('?') === -1 ? '?' : '&';
    var full = url + sep + 'section_id=' + encodeURIComponent(st.sectionId);
    function once() {
      st.controller = new AbortController();
      return fetch(full, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: st.controller.signal
      }).then(function (response) {
        if (!response.ok) throw new Error('infinite request failed: ' + response.status);
        return response.text();
      }).then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var freshRoot = st.sectionId ? doc.querySelector('[data-section-id="' + st.sectionId + '"]') : null;
        var freshList = freshRoot ? freshRoot.querySelector('.cm-grid, .cm-result-list') : doc.querySelector('.cm-grid, .cm-result-list');
        if (!freshList) {
          // A well-formed section with NO list is either past the real end
          // (search announces more pages than it returns) or a transient empty
          // render under edge load. Both go through the quiet retries first;
          // only if it stays empty is it the end (see loadNext) — never an error.
          var emptyErr = new Error('infinite: empty section response');
          emptyErr.cmEmpty = !!(freshRoot || doc.querySelector('.shopify-section'));
          throw emptyErr;
        }
        return freshList;
      });
    }
    // one quiet retry after 1.2s: edge bursts / flaky mobile networks answer
    // 403/5xx to a request the next one serves fine; the skeleton stays up
    // meanwhile and the user only sees the error line if both attempts fail
    // (R1b: three quiet retries 1.2s / 3.5s / 8s — a rate-limit burst (403/429)
    // clears within seconds and the list must still reach its end)
    var delays = [1200, 3500, 8000];
    function attempt(err, n) {
      if (err && err.name === 'AbortError') return Promise.reject(err);
      if (n >= delays.length) return Promise.reject(err);
      return new Promise(function (resolve, reject) {
        setTimeout(function () {
          if (st.dead) { reject(err); return; }
          once().then(resolve, function (e2) { attempt(e2, n + 1).then(resolve, reject); });
        }, delays[n]);
      });
    }
    return once().catch(function (err) { return attempt(err, 0); });
  }

  /* Move the fetched page's <li> cards into the live list. Handles already
     seen are dropped; promo tiles stay page-1-only. Returns how many <li>
     landed. */
  function appendCards(st, freshList, replace) {
    var appended = 0;
    var frag = document.createDocumentFragment();
    st.lastAppend = { nodes: [], handles: [] }; /* QC-R2 C8: lets landPage undo an append that would move what is on screen */
    // snapshot: .children is a LIVE HTMLCollection — adoptNode() shrinks it
    // mid-loop and every second card would be skipped (the 12-of-24 bug)
    var items = Array.prototype.slice.call(freshList.children);
    if (replace) {
      // the seeded handles belonged to the server-rendered page N that is
      // being replaced — drop them or the re-fetched pages dedupe to nothing
      st.seen.clear();
      while (st.list.firstChild) st.list.removeChild(st.list.firstChild);
    }
    for (var i = 0; i < items.length; i += 1) {
      var li = items[i];
      if (li.tagName !== 'LI') continue;
      if (!replace && li.classList.contains('cm-grid__item--promo')) continue;
      var card = li.querySelector('[data-product-handle]');
      var handle = card ? card.getAttribute('data-product-handle') : '';
      if (handle) {
        if (st.seen.has(handle)) continue;
        st.seen.add(handle);
      }
      frag.appendChild(document.adoptNode(li));
      st.lastAppend.nodes.push(li);
      if (handle) st.lastAppend.handles.push(handle);
      appended += 1;
    }
    st.list.appendChild(frag);
    return appended;
  }

  function announce(st, count) {
    if (!st.status || !st.tplLoaded || !(count > 0)) return;
    st.status.textContent = st.tplLoaded.replace('__COUNT__', String(count));
  }

  function showEnd(st) {
    st.done = true;
    if (st.stage) st.stage.hidden = true;
    if (st.endEl) st.endEl.hidden = false;
    if (st.io) st.io.disconnect();
    fillGaps(st);
  }

  /* Relevance-sorted search is not stable between requests (ties re-order), so
     page 1 (server render) + pages 2..N (later requests) can drop a product
     and repeat another. At the end, if the announced total is a few handles
     above what is on screen, re-scan the pages ONCE for the missing handles
     (appendCards dedupes, so nothing repeats). */
  function fillGaps(st) {
    if (st.gapDone || st.dead) return;
    st.gapDone = true;
    var total = parseInt(st.footer.getAttribute('data-inf-total'), 10) || 0;
    var missing = total - st.seen.size;
    if (missing < 1 || missing > 5) return;
    var p = 1;
    (function step() {
      if (st.dead || st.seen.size >= total || p > st.pages) return;
      var page = p;
      p += 1;
      fetchPage(st, page).then(function (list) {
        /* QC-R2 C8: through the same anchored, shift-checked landing as a normal page */
        return landPage(st, list, 0);
      }).catch(function () { /* best effort */ }).then(function () {
        setTimeout(step, 400);
      });
    })();
  }

  function setError(st, on) {
    if (on) {
      st.footer.setAttribute('data-inf-state', 'error');
      if (st.errorEl) st.errorEl.hidden = false;
    } else {
      st.footer.removeAttribute('data-inf-state');
      if (st.errorEl) st.errorEl.hidden = true;
    }
  }

  /* ------------------------------------------------------------ *
   * Re-arm: geometry check that does not depend on IO transitions
   * ------------------------------------------------------------ */
  function sentinelDue(st) {
    if (st.dead || st.done || st.loading || st.restoring) return false;
    if (!st.sentinel || !st.stage || st.stage.hidden) return false;
    if (st.footer.getAttribute('data-inf-state') === 'error') return false; // retry is the link's job
    if (st.root.classList.contains('is-loading')) return false;
    if (!st.sentinel.getClientRects().length) return false; // not rendered (hidden tab / display:none)
    return st.sentinel.getBoundingClientRect().top < window.innerHeight + ROOT_PX;
  }

  function scheduleRecheck(st) {
    setTimeout(function () {
      if (sentinelDue(st)) loadNext(st, false);
    }, 80);
  }

  /* The IntersectionObserver only sees the sentinel while it is inside the
     viewport + 600px BELOW. A jump past it (End key, back-to-top then drag,
     a fling, a tall footer) leaves it above the viewport and the observer
     never fires: this passive, throttled scroll check closes that gap. */
  var scrollBound = false;
  function bindScrollRecheck() {
    if (scrollBound) return;
    scrollBound = true;
    var waiting = false;
    window.addEventListener('scroll', function () {
      if (waiting) return;
      waiting = true;
      setTimeout(function () {
        waiting = false;
        LIVE.forEach(function (st) {
          if (sentinelDue(st)) loadNext(st, false);
        });
      }, 150);
    }, { passive: true });
  }

  /* Called after anything that moves the sentinel without scrolling (gender
     chip, layout toggle, filter swap). */
  function kick() {
    LIVE.forEach(function (st) {
      if (!st.footer.isConnected || !st.list.isConnected) {
        kill(st);
        return;
      }
      scheduleRecheck(st);
    });
  }

  /* Fetch the next page now (probe), whatever the scroll position: the gender
     tabs on a collection need to know whether a second gender exists before
     they decide to show themselves. */
  function more() {
    var started = 0;
    LIVE.forEach(function (st) {
      if (st.dead || st.done || st.loading || st.restoring) return;
      if (st.footer.getAttribute('data-inf-state') === 'error') return;
      started += 1;
      loadNext(st, true);
    });
    return started;
  }

  /* Eager mode (data-inf-eager on the root = grouped search "All"): load every remaining
     page one after the other, up to EAGER_PAGES, so the gender groups are complete
     and their counts exact. Normal sentinel loading keeps going beyond the cap.
     M-perf: it no longer starts at load (13 section requests on a 312-result search
     inside the LCP / TBT window, and for shoppers who never scroll). It starts on
     the first user input, never with Save-Data, and sits behind the same reserved
     space and scroll anchor as the sentinel loads. A page that lands while the
     shopper is deep in the grouped list can still insert cards above what is on
     screen, which is what the up-front prefetch hid (measured: CLS 0.4-2.4 when
     every page is next-page-only in this view). */
  /* L2 QC item 6: the grouped "All" view re-orders by gender, so a page that lands
     while the shopper is deep in the list inserts men's / women's cards ABOVE the
     visible kids group (0.5-0.9 layout shift per late page in the audit harness,
     only on the pages that arrive after the shopper has scrolled far). Eager mode
     therefore fetches EVERY page (up to EAGER_PAGES), three at a time, right
     after load and appends them in order, so the list is complete before anyone
     is deep in it. */
  var EAGER_PAGES = 60; /* QC-R2: coordinator decision, every result page */
  var EAGER_PARALLEL = 3;
  function eagerAll(st) {
    var limit = Math.min(st.pages, EAGER_PAGES);
    var ahead = {};
    function kickAhead() {
      for (var q = st.page + 1; q <= Math.min(limit, st.page + EAGER_PARALLEL); q += 1) {
        if (!ahead[q]) {
          ahead[q] = fetchPage(st, q);
          ahead[q].catch(function () { /* surfaced by loadNext when it awaits this page */ });
        }
      }
    }
    (function step() {
      if (st.dead || st.done || st.footer.getAttribute('data-inf-state') === 'error') return;
      if (st.page >= limit) return;
      if (st.loading || st.restoring) { setTimeout(step, 150); return; }
      kickAhead();
      var want = st.page + 1;
      loadNext(st, true, false, ahead[want]).then(function () { delete ahead[want]; (window.requestIdleCallback || function (cb) { window.setTimeout(cb, 0); })(step, { timeout: 600 }); });
    })();
  }


  /* Gender totals for the grouped search (M-perf). The chips and the "(n)" counts used to be
     exact only after every page of cards had been fetched and appended. The alternate view
     templates/search.counts.liquid returns ONLY per-gender counts for 50 results (about 1 KB),
     so after load (idle) the totals are fetched two pages at a time and published once as
     document event cm:gender-totals {all, men, women, boys, girls, kids, unisex}; cm-gender-groups.js
     then shows the right chips and counts without waiting for the cards. */
  function fetchGenderTotals(st) {
    var total = parseInt(st.footer.getAttribute('data-inf-total'), 10) || 0;
    var pages = Math.max(1, Math.ceil(total / 50));
    var totals = { all: 0, men: 0, women: 0, boys: 0, girls: 0, kids: 0, unisex: 0 };
    var next = 1;
    var done = 0;
    var failed = false;
    function urlFor(page) {
      var url = new URL(location.href);
      url.searchParams.set('view', 'counts');
      url.searchParams.set('page', String(page));
      url.searchParams.delete('section_id');
      url.searchParams.delete('gender');
      return url.pathname + url.search;
    }
    function worker() {
      if (failed || st.dead || next > pages) return Promise.resolve();
      var page = next;
      next += 1;
      return fetch(urlFor(page), { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (res) { if (!res.ok) throw new Error('counts ' + res.status); return res.text(); })
        .then(function (text) {
          var data = JSON.parse(text);
          Object.keys(totals).forEach(function (k) { if (k !== 'all') totals[k] += data[k] || 0; });
          totals.all += (data.men || 0) + (data.women || 0) + (data.boys || 0) + (data.girls || 0) + (data.kids || 0) + (data.unisex || 0);
          done += 1;
          return worker();
        });
    }
    Promise.all([worker(), worker()]).then(function () {
      if (failed || st.dead || done < pages) return;
      window.CM = window.CM || {};
      window.CM.genderTotals = totals;
      document.dispatchEvent(new CustomEvent('cm:gender-totals', { detail: totals }));
    }).catch(function () { failed = true; /* the loaded-cards counts keep working */ });
  }

  function armGenderTotals(st) {
    var run = function () { (window.requestIdleCallback || function (cb) { window.setTimeout(cb, 300); })(function () { fetchGenderTotals(st); }, { timeout: 3000 }); };
    if (document.readyState === 'complete') run(); else window.addEventListener('load', run, { once: true });
  }

  var anchorScrollAt = -1e9;

  function armEager(st) {
    armGenderTotals(st);
    var conn = navigator.connection || {};
    // Save-Data and slow-2g / 2g: the sentinel loads one page at a time (and no counts)
    if (conn.saveData || /(^|-)2g$/.test(String(conn.effectiveType || ''))) return;
    /* QC-R2 C8 (coordinator decision): prefetch + append EVERY result page before the first scroll, so
       no page lands while the shopper is scrolling the grouped list (the late pages were the 0.2-0.9
       shifts). It never runs in the LCP window: it starts on requestIdleCallback after window load,
       a further 1.5 s after load (LCP is final by then), or on the first user input if that comes first.
       Pages are fetched 3 ahead (network only) and appended one per idle slice (see eagerAll). */
    var started = false;
    function go() {
      if (started) return;
      started = true;
      ['scroll', 'wheel', 'touchstart', 'pointerdown', 'keydown'].forEach(function (type) {
        window.removeEventListener(type, go);
      });
      if (!st.dead) eagerAll(st);
    }
    ['scroll', 'wheel', 'touchstart', 'pointerdown', 'keydown'].forEach(function (type) {
      window.addEventListener(type, go, { passive: true, once: false });
    });
    function afterLoad() {
      window.setTimeout(function () {
        (window.requestIdleCallback || function (cb) { window.setTimeout(cb, 200); })(go, { timeout: 4000 });
      }, 1500);
    }
    if (document.readyState === 'complete') afterLoad();
    else window.addEventListener('load', afterLoad, { once: true });
  }

  /* Scroll anchor (M-perf). The grouped search view orders cards by gender with CSS
     `order` and adds heading rows, so a page that arrives while the shopper is deep in
     the list can insert men's / women's cards (or a heading) ABOVE the visible kids group
     and push it down. Native scroll anchoring is off here (card-entry transforms, sticky
     chrome), so do it by hand:
     remember where the first visible card is, and after the DOM change scroll by
     exactly how far it moved, in the same task / frame, before anything paints. A
     page that only adds cards BELOW the viewport moves nothing (dy is 0, no scroll);
     plain collections skip the measurement altogether (grouped lists only). */
  function anchorOf(root) {
    var items = root.querySelectorAll('.cm-grid__item, .cm-result-list > li');
    var vh = window.innerHeight || 800;
    var best = null;
    var bestTop = Infinity;
    for (var i = 0; i < items.length; i += 1) {
      var el = items[i];
      if (el.hidden || el.style.display === 'none') continue;
      var r = el.getBoundingClientRect();
      if (r.height <= 0 || r.bottom <= 0 || r.top >= vh) continue; // not on screen
      if (r.top < bestTop) { best = el; bestTop = r.top; }
    }
    return best ? { el: best, top: bestTop } : null;
  }

  function holdAnchor(a) {
    if (!a || !a.el.isConnected || a.el.style.display === 'none') return;
    var dy = a.el.getBoundingClientRect().top - a.top;
    if (Math.abs(dy) > 0.5) {
      anchorScrollAt = performance.now();
      window.scrollTo({ top: window.scrollY + dy, left: window.scrollX, behavior: 'instant' });
    }
  }

  /* ------------------------------------------------------------ *
   * CLS guard (L2 QC item 6). Cards are appended ABOVE the stage, so a stage
   * that is painted (skeleton row) while on screen is pushed away and Chrome
   * scores it (0.24 per append, up to 0.64 per page). Fix: before the fetch the
   * stage reserves the height of the previous batch and fills it with skeleton
   * cards as an OUT-OF-FLOW overlay (.cm-inf__sk is absolute) over a reserved
   * margin (a margin is not a box, so it cannot be scored); the append and the
   * release of the reservation happen in the SAME task, so the footer / SEO block
   * below only moves by the (small) estimate error and the vanishing skeleton is a
   * removal, not a shift.
   * Reserved only while the stage is still below the viewport, so starting the
   * reservation itself never shifts something the shopper can see.
   * ------------------------------------------------------------ */
  function reserveSpace(st) {
    if (!st.stage || !st.sk || st.restoring || st.reserved) return;
    var h = Math.round(st.batchH || st.list.offsetHeight || 0);
    if (h < 160) return;
    if (st.stage.getBoundingClientRect().top < window.innerHeight) return; // visible: do not move what is on screen
    var first = st.sk.querySelector('.cm-inf__sk-card:not([data-cm-clone])');
    if (!first) return;
    st.sk.hidden = false;
    var cols = (getComputedStyle(st.sk).gridTemplateColumns || '').split(' ').length || 2;
    var rowH = first.getBoundingClientRect().height + parseFloat(getComputedStyle(st.sk).rowGap || '0');
    var have = st.sk.children.length;
    var want = Math.min(48, Math.max(cols, Math.ceil(h / Math.max(rowH, 1)) * cols));
    for (var i = have; i < want; i += 1) {
      var c = first.cloneNode(true);
      c.setAttribute('data-cm-clone', '');
      st.sk.appendChild(c);
    }
    st.sk.style.maxBlockSize = h + 'px';
    st.sk.style.overflow = 'hidden';
    st.footer.style.marginBlockEnd = h + 'px'; /* a margin has no box: nothing for the shift tracker to score (a margin on the stage collapses away) */
    st.reserved = true;
  }

  function releaseSpace(st) {
    if (!st.stage) return;
    st.footer.style.marginBlockEnd = '';
    if (st.sk) {
      st.sk.style.maxBlockSize = '';
      st.sk.style.overflow = '';
      var clones = st.sk.querySelectorAll('[data-cm-clone]');
      for (var i = 0; i < clones.length; i += 1) clones[i].remove();
    }
    st.reserved = false;
  }

  function keepResidual(st, px) {
    if (!st.footer || px < 8) return;
    st.footer.style.marginBlockEnd = Math.round(px) + 'px';
    if (st.residualIO) { st.residualIO.disconnect(); st.residualIO = null; }
    if (!('IntersectionObserver' in window)) { st.footer.style.marginBlockEnd = ''; return; }
    st.residualIO = new IntersectionObserver(function (entries) {
      var visible = entries.some(function (e) { return e.isIntersecting; });
      if (visible) return;
      /* footer ABOVE the viewport (the shopper is already past it): clearing would move what they see */
      if (entries.some(function (e) { return e.boundingClientRect && e.boundingClientRect.top <= 0; })) return;
      /* only clear it while no new reservation replaced it */
      if (!st.reserved) st.footer.style.marginBlockEnd = '';
      st.residualIO.disconnect();
      st.residualIO = null;
    }, { rootMargin: '0px' });
    st.residualIO.observe(st.footer);
  }

  /* QC-R2 C8: items (cards, group heading rows) that are on screen right now, with their top. */
  function visibleTops(root) {
    var vh = window.innerHeight || 800;
    var out = [];
    var els = root.querySelectorAll('.cm-grid__item, .cm-grid__gender-head');
    for (var i = 0; i < els.length && out.length < 80; i += 1) {
      var el = els[i];
      if (el.hidden || el.style.display === 'none') continue;
      var r = el.getBoundingClientRect();
      if (r.height <= 0 || r.bottom <= 0 || r.top >= vh) continue;
      out.push({ el: el, top: r.top });
    }
    return out;
  }

  function movedAny(before) {
    for (var i = 0; i < before.length; i += 1) {
      var b = before[i];
      if (!b.el.isConnected) continue;
      if (Math.abs(b.el.getBoundingClientRect().top - b.top) > 1.5) return true;
    }
    return false;
  }

  /* Lands one fetched page in the list and resolves with the number of cards appended.
     Grouped (eager) list only: the cards of a gender go into THAT gender's block, so when a block
     boundary is on screen the cards below the insertion point are pushed down while the scroll
     anchor keeps the ones above still (shifts of 0.1-0.9 for a fast scroller). The append is
     measured in the same task (nothing paints in between); if any visible item moved after the
     anchor was held, it is undone and retried a moment later (the list keeps scrolling, the insertion
     point leaves the screen). After 60 tries (15 s) it lands anyway. */
  function footerBusy(on) {
    var root = document.documentElement;
    if (on) { root.setAttribute('data-cm-inf-busy', ''); return; }
    /* two frames later: the landing frame is painted with the footer hidden, then it reappears (a node that was not visible cannot shift) */
    var off = function () { root.removeAttribute('data-cm-inf-busy'); };
    if (window.requestAnimationFrame) window.requestAnimationFrame(function () { window.requestAnimationFrame(off); });
    else window.setTimeout(off, 50);
  }

  function landPage(st, freshList, tries) {
    var siteFooter = document.querySelector('.shopify-section-group-footer-group');
    if (siteFooter && siteFooter.getBoundingClientRect().top < (window.innerHeight || 800) + 200) footerBusy(true);
    var guard = st.eager && !st.restoring && tries < 60;
    var anchor = st.restoring || !st.eager ? null : anchorOf(st.root);
    var before = guard ? visibleTops(st.root) : null;
    var y0 = window.scrollY;
    var listH0 = st.list.offsetHeight;
    var reservedH = st.reserved ? (parseFloat(st.footer.style.marginBlockEnd) || 0) : 0;
    var count = appendCards(st, freshList, false);
    var listH1 = st.list.offsetHeight;
    holdAnchor(anchor);
    if (guard && before && before.length && count > 0 && movedAny(before)) {
      var undo = st.lastAppend || { nodes: [], handles: [] };
      var again = document.createElement('ul');
      undo.nodes.forEach(function (n) { again.appendChild(n); });
      undo.handles.forEach(function (h) { st.seen.delete(h); });
      window.scrollTo({ top: y0, left: window.scrollX, behavior: 'instant' });
      return new Promise(function (resolve) { setTimeout(resolve, 250); }).then(function () {
        if (st.dead || !st.list.isConnected) return 0;
        return landPage(st, again, tries + 1);
      });
    }
    if (listH1 > listH0) st.batchH = listH1 - listH0;
    releaseSpace(st);
    footerBusy(false);
    /* the reservation is an estimate (previous batch). When it was larger than what landed,
       releasing all of it pulled the footer / SEO block UP into view (shift 0.09-0.5 for a fast
       scroller). Keep the difference as a margin while the footer is on screen and drop it once the
       footer is off screen (invisible then). */
    if (reservedH > 0 && listH1 - listH0 + 8 < reservedH) keepResidual(st, reservedH - (listH1 - listH0));
    return Promise.resolve(count);
  }

  /* ------------------------------------------------------------ *
   * Load the next page (sentinel or Load-more click)
   * ------------------------------------------------------------ */
  function loadNext(st, silent, duringRestore, prefetched) {
    // duringRestore: the back-navigation chain drives loadNext itself while the
    // sentinel stays suppressed (st.restoring) until the rebuild finishes
    if (st.loading || (st.restoring && !duringRestore) || st.done || st.dead) return Promise.resolve(0);
    var next = st.page + 1;
    if (next > st.pages) {
      showEnd(st);
      return Promise.resolve(0);
    }
    st.loading = true;
    setError(st, false);
    if (st.stage) st.stage.hidden = false;
    /* QC-R2 C8: a fast scroller can have the site footer on screen while a page is still in flight.
       The page then lands above it and the footer jumps off screen (a shift of the whole footer, 0.2-0.5).
       While such a fetch runs the footer is hidden (visibility, its box stays), so nothing visible moves
       when the cards land; it comes back right after the append. */
    var siteFooter = document.querySelector('.shopify-section-group-footer-group');
    if (siteFooter && siteFooter.getBoundingClientRect().top < (window.innerHeight || 800)) {
      document.documentElement.setAttribute('data-cm-inf-busy', '');
    }
    reserveSpace(st);
    if (st.sk) st.sk.hidden = false;
    return (prefetched || fetchPage(st, next)).then(function (freshList) {
      if (st.dead || !st.footer.isConnected || !st.list.isConnected) {
        kill(st);
        return 0;
      }
      return landPage(st, freshList, 0).then(function (count) {
      st.page = next;
      st.autoRetries = 0;

      if (st.sk) st.sk.hidden = true;
      if (st.stage) st.stage.hidden = st.done;
      if (st.moreLink) {
        var follow = next + 1 <= st.pages ? next + 1 : next;
        st.moreLink.setAttribute('href', urlWithPage(follow));
      }
      if (!st.eager) {
        try {
          history.replaceState({ cmInfPage: st.page }, '', urlWithPage(st.page));
        } catch (e) { /* some engines refuse custom state */ }
      }
      if (!silent) announce(st, count);
      if (st.page >= st.pages) showEnd(st);
      // re-arm: an IntersectionObserver only fires on a visibility CHANGE. When
      // the new page leaves the sentinel inside the 600px margin (or above the
      // viewport, e.g. a gender chip hides most cards) it never fires again and
      // the list stalls (search 48 of 101). Re-check by geometry after layout.
      scheduleRecheck(st);
      return count;
      });
    }).catch(function (err) {
      if (err && err.name === 'AbortError') return 0;
      if (st.dead) return 0;
      if (err && err.cmEmpty) {
        // still empty after the retries: the list really ended before the
        // announced last page — end state with the real total, no error line
        releaseSpace(st);
        document.documentElement.removeAttribute('data-cm-inf-busy');
        if (st.sk) st.sk.hidden = true;
        if (st.endEl) {
          var real = st.list.querySelectorAll('[data-product-handle]').length;
          if (real > 0) st.endEl.textContent = st.endEl.textContent.replace(/\d+/, String(real));
        }
        st.page = st.pages;
        showEnd(st);
        return 0;
      }
      releaseSpace(st);
      document.documentElement.removeAttribute('data-cm-inf-busy');
      if (st.sk) st.sk.hidden = true;
      // The Load more link returns (CSS data-inf-state=error); clicking it retries.
      setError(st, true);
      // ...and the list also heals itself: the edge may rate-limit a fast
      // scroller (HTTP 429) for a few seconds; retry after 10s / 20s / 30s / 40s (x8) so
      // the list still reaches its end without the shopper having to tap.
      st.autoRetries = (st.autoRetries || 0) + 1;
      if (st.autoRetries <= 8) {
        setTimeout(function () {
          if (st.dead || st.done || !st.footer.isConnected) return;
          if (st.footer.getAttribute('data-inf-state') !== 'error') return;
          setError(st, false);
          loadNext(st, true);
        }, Math.min(10000 * st.autoRetries, 40000));
      }
      return 0;
    }).then(function (count) {
      st.loading = false;
      return count;
    });
  }

  /* ------------------------------------------------------------ *
   * Back-navigation restore — sessionStorage {page, scrollY}
   * ------------------------------------------------------------ */
  function storeKey() {
    return location.pathname + location.search;
  }

  function readStore() {
    try {
      var raw = sessionStorage.getItem(STORE_PREFIX + storeKey());
      var parsed = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed.y === 'number' && typeof parsed.page === 'number') return parsed;
    } catch (e) { /* storage blocked */ }
    return null;
  }

  function writeStore() {
    var st = pickActive();
    if (!st) return;
    try {
      sessionStorage.setItem(STORE_PREFIX + storeKey(), JSON.stringify({ page: st.page, y: Math.round(window.scrollY) }));
    } catch (e) { /* storage blocked */ }
  }

  function pickActive() {
    var best = null;
    LIVE.forEach(function (st) {
      if (!st.dead && st.footer.isConnected && st.list.isConnected) best = st;
    });
    return best;
  }

  function bindGlobalSave() {
    if (saveBound) return;
    saveBound = true;
    var last = 0;
    window.addEventListener('scroll', function () {
      if (!saveArmed) return;
      var now = Date.now();
      if (now - last < SAVE_THROTTLE_MS) return;
      last = now;
      writeStore();
    }, { passive: true });
    ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach(function (type) {
      window.addEventListener(type, function () { saveArmed = true; }, { passive: true });
    });
    window.addEventListener('pagehide', writeStore);
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') writeStore();
    });
  }

  function loadChain(st, count, startEpoch) {
    var chain = Promise.resolve();
    for (var i = 0; i < count; i += 1) {
      chain = chain.then(function () {
        if (st.dead || st.epoch !== startEpoch) return 0;
        return loadNext(st, true, true);
      });
    }
    return chain;
  }

  function restore(st) {
    if (st.page <= 1) return; // browser owns page-1 scroll restoration
    var stored = readStore();
    if (!stored) return; // deep link: keep the server-rendered page, load forward
    var target = Math.min(st.page, st.maxPages);
    if (target <= 1 || history.scrollRestoration === undefined) return;
    history.scrollRestoration = 'manual';
    st.restoring = true;
    var startEpoch = st.epoch;
    if (st.sk) st.sk.hidden = false;
    if (st.stage) st.stage.hidden = false;
    fetchPage(st, 1).then(function (freshList) {
      if (st.dead || st.epoch !== startEpoch) return 0;
      // the server rendered page N; rebuild from page 1 so the heights add up
      appendCards(st, freshList, true);
      st.page = 1;
      try {
        history.replaceState({ cmInfPage: 1 }, '', urlWithPage(1));
      } catch (e) { /* ignore */ }
      return loadChain(st, target - 1, startEpoch);
    }).then(function () {
      if (st.dead || st.epoch !== startEpoch) return;
      history.scrollRestoration = 'auto';
      // banners / lazy sections above the grid settle AFTER this restore and
      // scroll anchoring gives back the absolute position — re-apply the saved
      // y twice; any real user input cancels the re-apply immediately
      var target = stored.y;
      var cancelled = false;
      function cancel() { cancelled = true; }
      window.addEventListener('wheel', cancel, { once: true, passive: true });
      window.addEventListener('touchstart', cancel, { once: true, passive: true });
      window.addEventListener('keydown', cancel, { once: true });
      function settledY() {
        return Math.min(target, Math.max(0, document.documentElement.scrollHeight - window.innerHeight));
      }
      window.scrollTo(0, settledY());
      writeStore(); // the restored position is the new truth
      [800, 2000].forEach(function (delay) {
        setTimeout(function () {
          window.removeEventListener('wheel', cancel);
          window.removeEventListener('touchstart', cancel);
          window.removeEventListener('keydown', cancel);
          if (cancelled || st.dead || st.epoch !== startEpoch) return;
          if (Math.abs(window.scrollY - settledY()) <= 4) return;
          window.scrollTo(0, settledY());
        }, delay);
      });
    }).catch(function () {
      /* aborted or failed: keep whatever is on screen */
    }).then(function () {
      st.restoring = false;
      if (st.sk) st.sk.hidden = true;
      if (st.stage) st.stage.hidden = st.done;
    });
  }

  /* ------------------------------------------------------------ *
   * Lifecycle
   * ------------------------------------------------------------ */
  function kill(st) {
    if (st.dead) return;
    st.dead = true;
    st.epoch += 1;
    if (st.io) st.io.disconnect();
    if (st.controller) st.controller.abort();
    document.documentElement.removeAttribute('data-cm-inf-busy');
    LIVE.delete(st);
  }

  function init(el) {
    if (!el || el._cmInf) return;
    var root = el.closest('[data-cm-collection], [data-cm-search]');
    var list = el.previousElementSibling;
    if (!root || !list || list.tagName !== 'UL') return;
    var st = {
      footer: el,
      root: root,
      list: list,
      stage: el.querySelector('[data-cm-inf-stage]'),
      sentinel: el.querySelector('[data-cm-inf-sentinel]'),
      sk: el.querySelector('[data-cm-inf-sk]'),
      status: el.querySelector('[data-cm-inf-status]'),
      endEl: el.querySelector('[data-cm-inf-end]'),
      errorEl: el.querySelector('[data-cm-inf-error]'),
      moreLink: el.querySelector('[data-cm-inf-more]'),
      sectionId: root.getAttribute('data-section-id') || '',
      pages: parseInt(el.getAttribute('data-inf-pages'), 10) || 1,
      maxPages: parseInt(el.getAttribute('data-inf-max'), 10) || 8,
      tplLoaded: el.getAttribute('data-inf-loaded-tpl') || '',
      page: pageParam(),
      eager: root.hasAttribute('data-inf-eager'),
      seen: new Set(),
      loading: false,
      restoring: false,
      done: false,
      dead: false,
      epoch: 0,
      io: null,
      controller: null
    };
    el._cmInf = st;

    var seeded = list.querySelectorAll('[data-product-handle]');
    for (var i = 0; i < seeded.length; i += 1) st.seen.add(seeded[i].getAttribute('data-product-handle'));
    LIVE.add(st);
    bindGlobalSave();
    bindScrollRecheck();

    if (st.moreLink) {
      st.moreLink.addEventListener('click', function (event) {
        event.preventDefault();
        loadNext(st, false);
      });
    }

    if (!('IntersectionObserver' in window) || !st.sentinel) {
      el.classList.add('no-io'); // Load more link stays the loader
      return;
    }
    try {
      st.io = new IntersectionObserver(function (entries) {
        for (var j = 0; j < entries.length; j += 1) {
          if (!entries[j].isIntersecting) continue;
          if (!st.footer.isConnected || !st.list.isConnected) {
            kill(st);
            return;
          }
          if (st.loading || st.restoring || st.done) return;
          if (st.root.classList.contains('is-loading')) return; // a filter fetch owns the grid
          loadNext(st, false);
          return;
        }
      }, { root: null, rootMargin: ROOT_MARGIN, threshold: 0 });
      st.io.observe(st.sentinel);
    } catch (e) {
      el.classList.add('no-io');
      return;
    }

    restore(st);
    if (st.eager && !st.restoring) armEager(st);
  }

  function initAll(scope) {
    var where = scope && scope.querySelectorAll ? scope : document;
    var nodes = where.querySelectorAll('[data-cm-inf]');
    for (var i = 0; i < nodes.length; i += 1) init(nodes[i]);
  }

  function rescan(scope) {
    LIVE.forEach(function (st) {
      if (!st.footer.isConnected || !st.list.isConnected) kill(st);
    });
    initAll(scope || document);
  }

  if (window.CM && typeof window.CM.on === 'function') {
    // a filter / sort / tab swap re-rendered the footer at page 1
    window.CM.on('collection:updated', function () { rescan(document); });
  }
  document.addEventListener('shopify:section:load', function (event) {
    if (event && event.target) rescan(event.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  /* Export */
  if (window.CM) {
    window.CM.infinite = { init: initAll, kick: kick, more: more, anchorOf: anchorOf, holdAnchor: holdAnchor };
  }
})();
